import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Flex } from 'antd';
import classNames from 'classnames';
import { ContractTitle } from '@monorepo/client-types';
import {
  Modal,
  ModalHandle,
  Space,
  TextComponent,
} from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  Application,
  ContractData,
  ContractType,
  ThemeColors,
  User,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { useColor } from '../../hooks';
import { EnvService } from '../../services/core/env-service';
import FundedModalContent5ers from './funded-modal-content/funded-modal-content-5ers';
import FundedModalContentTtp from './funded-modal-content/funded-modal-content-ttp';
import FundedRulesModalContent from './funded-rules-modal-content/funded-rules-modal-content';
import NyseModalContent from './nyse-modal-content/nyse-modal-content';

import './contracts.scss';

const project = EnvService.application;

interface Props {
  contractType: ContractType;
}

const formDefaultValues = (contractType: ContractType, currentUser: User) => {
  const map: { [key: string]: any } = {
    [ContractType.Nyse]: {
      firstAgreement: false,
      receiveDataNonBusinessUse: false,
      hasSECOrCFTC: false,
      securityAgency: false,
      similarToSECOrCFTC: false,
      investmentAdvices: false,
      assetManager: false,
      usingOtherCapital: false,
      tradeForOtherCorpOrOther: false,
      sharingProfit: false,
      sharingSpace: false,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      address: currentUser.address,
      occupations: '',
      companyName: '',
      employer: '',
      employerAddress: '',
      title: '',
      signThatProvidedDataIsCorrect: false,
      finalAgreement: false,
    },
  };
  return map[contractType] || {};
};

const isContractWithData = (contractType: ContractType) => {
  return ContractType.Nyse === contractType;
};

const isContractSigned = (
  contractType: ContractType,
  acceptedTermsAndConditions: boolean,
  form: UseFormReturn<ContractData>,
) => {
  if (ContractType.Nyse === contractType) {
    return form.watch('finalAgreement');
  }
  return acceptedTermsAndConditions;
};

const ContractModal: FunctionComponent<Props> = ({ contractType }) => {
  const modalRef = useRef<ModalHandle>(null);
  const { getColor } = useColor();

  const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] =
    useState(false);
  const { updateLoadingState, isLoading } = useLoading(LoadingState.Initial);
  const {
    dataStore: { userStore, contractStore },
  } = useStore();
  const { t } = useTranslation(['affiliate', 'contractModal']);

  const form = useForm<ContractData>({
    mode: 'all',
    defaultValues: formDefaultValues(contractType, userStore.currentUser),
  });

  const contractWithData = isContractWithData(contractType);

  const contractSigned = isContractSigned(
    contractType,
    acceptedTermsAndConditions,
    form,
  );

  useEffect(() => {
    if (contractStore.contractSignNeeded(contractType)) {
      modalRef.current?.open();
    }
  }, [contractStore, contractType]);

  const handleContractSign = async () => {
    try {
      const contractData = form.getValues();
      updateLoadingState(LoadingState.Loading);
      await contractStore.signUserContract(contractType, contractData);
      updateLoadingState(LoadingState.Loaded);
    } catch (e) {
      updateLoadingState(LoadingState.Error);
      console.error(e);
    }
  };

  const getContract = (): {
    content: ReactNode;
    title: ContractTitle | ReactNode;
  } => {
    const defaultContract = { content: null, title: '' as ContractTitle };
    const fundedAccountsTitle = (
      <TextComponent
        level={2}
        weight={600}
        color={getColor(ThemeColors.colorPrimary)}
      >
        {t('affiliate:contract_funded_rules_modal.funded_accounts')}
      </TextComponent>
    );

    const ContractMap: Record<
      Application,
      Partial<
        Record<
          ContractType,
          { content: ReactNode; title: ContractTitle | ReactNode }
        >
      >
    > = {
      [Application.THE5ERS]: {
        [ContractType.Funded]: {
          content: <FundedModalContent5ers />,
          title: t(`contractModal:${ContractTitle.Funded}`),
        },
        [ContractType.FundedRules]: {
          content: <FundedRulesModalContent />,
          title: fundedAccountsTitle,
        },
      },
      [Application.TTP]: {
        [ContractType.Funded]: {
          content: <FundedModalContentTtp />,
          title: t(`contractModal:${ContractTitle.Funded}`),
        },
        [ContractType.FundedRules]: {
          content: <FundedRulesModalContent />,
          title: fundedAccountsTitle,
        },
        [ContractType.Nyse]: {
          content: <NyseModalContent form={form} />,
          title: t(`contractModal:${ContractTitle.Nyse}`),
        },
      },
    };

    const contracts = ContractMap[project] || {};
    return contracts[contractType] ?? defaultContract;
  };

  const contract = getContract();

  return (
    <Modal
      ref={modalRef}
      forceOk
      onOk={handleContractSign}
      title={contract.title}
      width={700}
      footer={
        contractSigned && (
          <Button
            disabled={isLoading}
            onClick={modalRef.current?.ok}
            loading={isLoading}
            className={classNames({
              'funded-rules-button': contractType === ContractType.FundedRules,
            })}
          >
            {t(
              contractType === ContractType.FundedRules
                ? 'affiliate:contract_funded_rules_modal.i_confirm_the_rules'
                : 'affiliate:contract_funded_rules_modal.accept',
            )}
          </Button>
        )
      }
    >
      <Space direction={'vertical'} size={20}>
        <div>{contract.content}</div>

        {!contractWithData && (
          <Flex gap={10}>
            <Checkbox
              onChange={() => {
                setAcceptedTermsAndConditions(!acceptedTermsAndConditions);
              }}
              value={acceptedTermsAndConditions}
            />
            <div>{t('affiliate:contract_funded_rules_modal.agree')}</div>
          </Flex>
        )}
      </Space>
    </Modal>
  );
};

export default ContractModal;
